import { useTranslation } from "@redotech/redo-i18n";
import { Condition } from "@redotech/redo-return-core-common/return-flow/condition";
import { ConditionState } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { AfterHoursDetails } from "@redotech/redo-return-ui/flows/return-flow/condition-types/after-hours";
import {
  DetailsComponentsForRuleConditions,
  MultipleConditionsState,
} from "@redotech/redo-return-ui/flows/rules/multiple-conditions";
import { TICKET_CHANNEL } from "@redotech/redo-return-ui/flows/rules/ticket-channel";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { ComponentType, SetStateAction, Suspense, lazy, memo } from "react";
import { AssignmentTypeDetails } from "./conditions/assignment-type";
import { MessageBodyMatchesDetails } from "./conditions/message-body-matches";
import { SentFromEmailDetails } from "./conditions/sent-from-email";
import { SubjectLineDetails } from "./conditions/subject-line";
import { ReceivedAtEmailDetails } from "./received-at-email";
import { TicketChannelDetails } from "./ticket-channel";
import { TopicDetails } from "./topic";

/**
 * Resolved by the bundler but not by TypeScript, so the static
 * support-app -> merchant-app compile cycle stays broken. Props are unchecked.
 */
const ConditionDetails: ComponentType<any> = lazy(() =>
  import(
    "@redotech/redo-merchant-app/setting/return-flow/condition" as string
  ).then((module) => ({ default: module.ConditionDetails })),
);

export const MultipleConditionsDetails = memo(
  function MultipleConditionsDetails({
    stepSelect,
    state,
    setState,
    flowType,
    step,
    allSteps,
    setStateWithId,
  }: StepTypeDetailsProps<MultipleConditionsState>) {
    const { t } = useTranslation();
    const handleAddCondition = () => {
      setState((oldState) => {
        return {
          ...oldState,
          conditions: [
            ...oldState.conditions,
            {
              conditionType: TICKET_CHANNEL(TicketChannelDetails),
              conditionState: TICKET_CHANNEL(TicketChannelDetails).fromModel({
                type: Condition.TICKET_CHANNEL,
                channels: [ConversationPlatform.INSTAGRAM],
              }),
            },
          ],
        };
      });
    };
    const handleDeleteCondition = (index: number) => {
      setState((oldState) => {
        return {
          ...oldState,
          conditions: oldState.conditions.filter((_, ind) => ind !== index),
        };
      });
    };
    return (
      <>
        {state.conditions.map((condition, index) => (
          <>
            <Divider />
            <Suspense fallback={null}>
              <ConditionDetails
                allSteps={allSteps}
                flowType={flowType}
                onDeleteChild={() => handleDeleteCondition(index)}
                setState={(
                  updateSingleCondition: SetStateAction<ConditionState>,
                ) => {
                  let updatedCondition: ConditionState;
                  if (typeof updateSingleCondition === "function") {
                    const updateConditionState =
                      updateSingleCondition(condition);
                    if (
                      typeof updateConditionState.conditionState === "function"
                    ) {
                      const updatedConditionState =
                        updateConditionState.conditionState(
                          condition.conditionState,
                        );
                      updatedCondition = {
                        ...updatedConditionState,
                        conditionState: updatedConditionState,
                      };
                    } else {
                      updatedCondition = updateConditionState;
                    }
                  } else {
                    updatedCondition = updateSingleCondition;
                  }
                  setState((oldState) => {
                    return {
                      ...oldState,
                      conditions: oldState.conditions.map((cond, ind) => {
                        if (index === ind) {
                          return updatedCondition;
                        } else {
                          return cond;
                        }
                      }),
                    };
                  });
                }}
                setStateWithId={setStateWithId}
                state={condition}
                step={step}
                stepSelect={stepSelect}
              />
            </Suspense>
          </>
        ))}
        <LabeledInput
          errors={
            state.conditions.length > 0
              ? []
              : [t("At least one condition is required")]
          }
          label=""
        >
          <RedoButton
            hierarchy="primary"
            onClick={handleAddCondition}
            size="md"
            text={t("+ Add condition", {
              context: "button label; keep the leading plus sign",
            })}
            theme="normal"
          />
        </LabeledInput>
        <LabeledInput
          errors={
            state.nextTrue || state.nextFalse
              ? []
              : [t("At least one of these is required")]
          }
          label={t("If matches, then")}
        >
          {stepSelect({
            value: state.nextTrue,
            valueChange: (nextTrue) =>
              setState((state) => ({ ...state, nextTrue })),
          })}
        </LabeledInput>
        <LabeledInput
          errors={
            state.nextTrue || state.nextFalse
              ? []
              : [t("At least one of these is required")]
          }
          label={t("If does not match, then")}
        >
          {stepSelect({
            value: state.nextFalse,
            valueChange: (nextFalse) =>
              setState((state) => ({ ...state, nextFalse })),
          })}
        </LabeledInput>
      </>
    );
  },
);

export const DetailsForRuleConditions: DetailsComponentsForRuleConditions = {
  TicketChannelDetails,
  AfterHoursDetails,
  SubjectLineDetails,
  MessageBodyMatchesDetails,
  TopicDetails,
  ReceivedAtEmailDetails,
  SentFromEmailDetails,
  AssignmentTypeDetails,
};
